import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  PushNotificationService,
  PushNotificationRecord,
  BroadcastPushPayload,
} from '../services/pushNotificationService';

interface UsePushNotificationsResult {
  pushNotifications: PushNotificationRecord[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  sendBroadcastPush: (payload: BroadcastPushPayload) => Promise<void>;
  deletePushNotification: (id: string) => Promise<void>;
}

export const usePushNotifications = (): UsePushNotificationsResult => {
  const [pushNotifications, setPushNotifications] = useState<
    PushNotificationRecord[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = PushNotificationService.subscribeToPushNotifications(
      (records) => {
        setPushNotifications(records);
        setLoading(false);
      },
      (subscriptionError) => {
        setError(subscriptionError.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const records = await PushNotificationService.getPushNotifications();
      setPushNotifications(records);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to refresh push notifications';
      setError(message);
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcastPush = async (payload: BroadcastPushPayload) => {
    try {
      await PushNotificationService.sendBroadcastPush(payload);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to send broadcast push notification';
      setError(message);
      console.error('Broadcast push error:', err);
      throw err;
    }
  };

  const deletePushNotification = async (id: string) => {
    try {
      await PushNotificationService.deletePushNotification(id);
      toast.success('Push notification deleted');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to delete push notification';
      setError(message);
      console.error('Delete push notification error:', err);
      throw err;
    }
  };

  return {
    pushNotifications,
    loading,
    error,
    refresh,
    sendBroadcastPush,
    deletePushNotification,
  };
};
